import { useState, useEffect } from 'react'
import WheelLoader from '../common/WheelLoader'

interface ProcessingModalProps {
  isOpen: boolean
  progress: number
  stage: string
}

const funFacts = [
  'The first resume was written by Leonardo da Vinci in 1482!',
  'Recruiters spend an average of 7 seconds on an initial resume scan.',
  'PDF stands for Portable Document Format, created by Adobe in 1993.',
  'Most hiring managers prefer resumes that are 1-2 pages long.',
  'Using action verbs can make your resume 140% more effective!',
  'Candidates with portfolio projects are 3x more likely to get interviews.',
  'Soft skills are now considered as important as technical skills by 93% of employers.',
  'A well-designed profile page can increase response rates by 40%.',
  'First impressions are formed in just 50 milliseconds!',
  'Your new profile is almost ready to share with the world!',
]

export default function ProcessingModal({ isOpen, progress, stage }: ProcessingModalProps) {
  const [currentFact, setCurrentFact] = useState('')

  useEffect(() => {
    if (isOpen) {
      setCurrentFact(funFacts[Math.floor(Math.random() * funFacts.length)])
      const interval = setInterval(() => {
        setCurrentFact(funFacts[Math.floor(Math.random() * funFacts.length)])
      }, 4000)
      return () => clearInterval(interval)
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-md" />

      {/* Modal */}
      <div
        className="relative bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 max-w-md w-full mx-4"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        {/* Animated wheel */}
        <div className="mb-8 flex justify-center">
          <WheelLoader width={170} />
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="h-2 bg-black/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-black rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-sm">
            <span className="text-black/60">Processing...</span>
            <span className="font-medium text-black">{progress}%</span>
          </div>
        </div>

        {/* Stage text */}
        <h3 className="text-xl font-semibold text-black text-center mb-3">{stage}</h3>

        {/* Fun fact */}
        <div className="bg-black/5 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-black/10 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <p className="text-sm text-black/60 leading-relaxed">{currentFact}</p>
          </div>
        </div>
      </div>

      {/* Keyframe animation */}
      <style>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  )
}
